import { Route, Routes, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout";
import { ChatSupportPage } from "../pages/chat-support/chat-support";
// import Employees from "../pages/employees";
import Guard from "./guard.route";
import { getLoggedInRoleKey } from "../utils/browserStorage.util";

import CoachRecordings from "src/features/coach-recordings/pages";
import { CreditVouchers } from "src/features/credit-vouchers/pages";
import { CreateCreditVouchers } from "src/features/credit-vouchers/pages/create";
import { RedeemCreditVoucher } from "src/features/credit-vouchers/pages/redeem";
import { UsersTransactions } from "src/features/users-transactions/pages";
import WithdrawalRequests from "src/features/withdrawal-requests/pages";
import Invoice from "src/features/withdrawal-requests/pages/id/invoice";
import LoginLayout from "src/components/layout/login-layout";
import Statistics from "src/features/statistics/pages";
import EnhancedDashboard from "src/features/statistics/pages/enhanced-dashboard";
import Sessions from "src/features/sessions/pages";
import SessionInfoPage from "src/features/sessions/pages/show";
import UsersPage from "src/features/users/pages";
import UserDetailsPage from "src/features/users/pages/id";
import UserBlocksPage from "src/features/users/pages/blocks";
import SpaceHistoryPage from "src/features/users/pages/space-switch-history";
import UserLearnerProgressPage from "src/features/users/pages/progress";
import MeetingPolicyPage from "src/features/users/pages/meeting-policy";
import CreateBookingsForLearnerPage from "src/features/bookings/pages/create-for-learner";
import SlotsPage from "src/features/slots/pages";
import SlotDetailsPage from "src/features/slots/pages/id";
import SlotRequestsPage from "src/features/slot-requests/pages";
import SlotRequestDetailsPage from "src/features/slot-requests/pages/id";
import AudienceHealthPage from "src/features/notification-health/pages";
import PromotersPage from "src/features/promoters/pages";
import CreatePromoterPage from "src/features/promoters/pages/create";
import PromoterDetailsPage from "src/features/promoters/pages/id";
import PaymentTransactionsPage from "src/features/payment-transactions/pages";
import PaymentTransactionDetailsPage from "src/features/payment-transactions/pages/id";
import BookingsPage from "src/features/bookings/pages";
import BookingDetailsPage from "src/features/bookings/pages/id";
import AISessionsPage from "src/features/ai-sessions/pages";
import AISessionDetailsPage from "src/features/ai-sessions/pages/id";
import WordBankPage from "src/features/word-bank/pages";
import SubscriptionUsersPage from "src/features/subscription-users/pages";
import SubscriptionUserDetailsPage from "src/features/subscription-users/pages/subscription-user-details";
import { LoungeRequests } from "src/features/lounge/pages";
import { LoungeRequest } from "src/features/lounge/pages/show";
import Rooms from "src/features/rooms/pages";
import ShowRoom from "src/features/rooms/pages/show";
import Posts from "src/features/feed/pages";
import { CreatePost } from "src/features/feed/pages/create";
import { EditScheduledPost } from "src/features/feed/pages/scheduled-posts/edit";
import OnboardingReportsPage from "src/features/onboarding-reports/pages";
import UsersReportsPage from "src/features/users-reports/pages";
import AITopicsPage from "src/features/ai-coach/topics/pages";
import AICustomTopicsPage from "src/features/ai-coach/custom-scenarios/pages";
import AILanguageVoicesPage from "src/features/ai-coach/voices/pages";
import AICoachPromptsPage from "src/features/ai-coach/prompts/pages";
import AICoachLevelVocabularyPage from "src/features/ai-coach/level-vocabulary/pages";
import ActiveSlotsReportsPage from "src/features/slots/pages/active-slots-reports.page";
import KarmaExchangesPage from "src/features/karma-exchanges/pages";
import KarmaExchangeDetailsPage from "src/features/karma-exchanges/pages/show";
import RewardsPage from "src/features/rewards/pages";
import CreateRewardPage from "src/features/rewards/pages/create";
import RewardDetailsPage from "src/features/rewards/pages/show";
import EditRewardPage from "src/features/rewards/pages/edit";
import RequestedRewardCountriesPage from "src/features/rewards/pages/requested-countries";
import FunctionErrorsPage from "src/features/function-errors/pages";
import RecruiterPage from "src/features/recruiter/pages";
import RecruiterRecordPage from "src/features/recruiter/pages/id";
import CoachSignupAttributionPage from "src/features/coach-signup-attribution/pages";
import BillingExceptionsPage from "src/features/billing-exceptions/pages";
import TaskFailuresPage from "src/features/task-failures/pages";
import TaskFailureDetailsPage from "src/features/task-failures/pages/id";
import IntegrationHealthPage from "src/features/integration-health/pages";
import AdminEmailPage from "src/features/admin-email/pages";

// Unauthorized page component that redirects employees
const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const role = getLoggedInRoleKey();
  
  if (process.env.NODE_ENV === 'development') {
    console.log("UnauthorizedPage rendered, role:", role);
  }
  
  useEffect(() => {
    // Immediately redirect employees to statistics
    if (role === "employee") {
      if (process.env.NODE_ENV === 'development') {
        console.log("Redirecting employee from UnauthorizedPage to /statistics");
      }
      navigate("/statistics", { replace: true });
    }
  }, [role, navigate]);
  
  // If employee, show nothing while redirecting
  if (role === "employee") {
    return null;
  }
  
  // For other roles, show unauthorized message
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Unauthorized</h1>
        <p className="text-gray-600">You don't have permission to access this page.</p>
        <p className="text-sm text-gray-500 mt-2">Role: {role || "none"}</p>
      </div>
    </div>
  );
};

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginLayout></LoginLayout>} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      {/* Employee routes - only statistics pages */}
      <Route element={<Guard allowedRoles={["employee"]} />}>
        <Route path="/*" element={<Layout />}>
          <Route index element={<Navigate to="/statistics" replace />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="graphical-statistics" element={<EnhancedDashboard />} />
          {/* For employees, redirect any unmatched routes to statistics */}
          <Route path="*" element={<Navigate to="/statistics" replace />} />
        </Route>
      </Route>

      {/* Admin routes - all other routes */}
      <Route element={<Guard allowedRoles={["admin"]} />}>
        <Route path="/*" element={<Layout />}>
          <Route index element={<Navigate to="/statistics" replace />} />
          {/* <Route path="admins/*">
            <Route index element={<Admins />} />
          
          {/* Add the new UsersList route */}
          <Route path="users/*">
            <Route index element={<UsersPage />} />
            <Route path=":userId" element={<UserDetailsPage />} />
            <Route path=":userId/blocks" element={<UserBlocksPage />} />
            <Route path=":userId/space-switch-history" element={<SpaceHistoryPage />} />
            <Route path=":userId/progress" element={<UserLearnerProgressPage />} />
            <Route path=":userId/meeting-policy" element={<MeetingPolicyPage />} />
            <Route path=":userId/create-bookings" element={<CreateBookingsForLearnerPage />} />
          </Route>

          {/* Add the new PromotersList route */}
          <Route path="promoters/*">
            <Route index element={<PromotersPage />} />
            <Route path="create" element={<CreatePromoterPage />} />
            <Route path=":promoterId" element={<PromoterDetailsPage />} />
          </Route>

          <Route path="withdrawal-requests">
            <Route index element={<WithdrawalRequests />} />
            <Route path=":id/*">
              <Route path="invoice" element={<Invoice />} />
            </Route>
          </Route>
          <Route path="karma-exchanges/*">
            <Route index element={<KarmaExchangesPage />} />
            <Route path=":id" element={<KarmaExchangeDetailsPage />} />
          </Route>
          <Route path="rewards/*">
            <Route index element={<RewardsPage />} />
            <Route path="requested-countries" element={<RequestedRewardCountriesPage />} />
            <Route path="create" element={<CreateRewardPage />} />
            <Route path=":rewardId" element={<RewardDetailsPage />} />
            <Route path=":rewardId/edit" element={<EditRewardPage />} />
          </Route>
          <Route path="chat-support/*">
            <Route index element={<ChatSupportPage />} />
          </Route>
          <Route path="recruiter/*">
            <Route index element={<RecruiterPage />} />
            <Route path=":kind/:id" element={<RecruiterRecordPage />} />
          </Route>

          <Route path="coach-records/*">
            <Route index element={<CoachRecordings />} />
          </Route>
          <Route path="credit-vouchers/*">
            <Route index element={<CreditVouchers />} />
            <Route path="create" element={<CreateCreditVouchers />} />
            <Route path="redeem/:userId" element={<RedeemCreditVoucher />} />
          </Route>
          <Route path="users-transactions/*">
            <Route index element={<UsersTransactions />} />
          </Route>
          <Route path="slots/*">
            <Route index element={<SlotsPage />} />
            <Route path=":id" element={<SlotDetailsPage />} />
          </Route>
          <Route path="slot-requests/*">
            <Route index element={<SlotRequestsPage />} />
            <Route path=":id" element={<SlotRequestDetailsPage />} />
          </Route>
          <Route path="audience-health" element={<AudienceHealthPage />} />
          <Route path="active-slots-reports/*">
            <Route index element={<ActiveSlotsReportsPage />} />
          </Route>
          <Route path="function-errors">
            <Route index element={<FunctionErrorsPage />} />
          </Route>
          <Route path="task-failures/*">
            <Route index element={<TaskFailuresPage />} />
            <Route path=":id" element={<TaskFailureDetailsPage />} />
          </Route>
          <Route path="coach-signup-attribution">
            <Route index element={<CoachSignupAttributionPage />} />
          </Route>
          <Route path="payment-transactions/*">
            <Route index element={<PaymentTransactionsPage />} />
            <Route path=":id" element={<PaymentTransactionDetailsPage />} />
          </Route>
          <Route path="billing-exceptions" element={<BillingExceptionsPage />} />
          <Route path="integration-health" element={<IntegrationHealthPage />} />
          <Route path="admin-email" element={<AdminEmailPage />} />
          <Route path="bookings/*">
            <Route index element={<BookingsPage />} />
            <Route path=":bookingId" element={<BookingDetailsPage />} />
          </Route>
          <Route path="ai-sessions/*">
            <Route index element={<AISessionsPage />} />
            <Route path=":aiSessionId" element={<AISessionDetailsPage />} />
          </Route>
          <Route path="word-bank/*">
            <Route index element={<WordBankPage />} />
          </Route>
          <Route path="subscription-users/*">
            <Route index element={<SubscriptionUsersPage />} />
            <Route path=":id" element={<SubscriptionUserDetailsPage />} />
          </Route>
          {/* <Route path="/*" index element={<Statistics />} /> */}
          <Route path="sessions/*">
            <Route index element={<Sessions />} />
            <Route path=":id" element={<SessionInfoPage />}></Route>
          </Route>
          <Route path="lounge-requests/*">
            <Route index element={<LoungeRequests />} />
            <Route path=":id" element={<LoungeRequest />}></Route>
          </Route>
          <Route path="rooms/*">
            <Route index element={<Rooms />} />
            <Route path=":id" element={<ShowRoom />} />
          </Route>
          <Route path="statistics" element={<Statistics />} />
          <Route path="graphical-statistics" element={<EnhancedDashboard />} />
          <Route path="feed">
            <Route index element={<Posts />} />
            <Route path="create" element={<CreatePost />} />
            <Route path="scheduled-posts">
              <Route path=":postId">
                <Route path="edit" element={<EditScheduledPost />} />
              </Route>
            </Route>
          </Route>
          <Route path="onboarding-reports">
            <Route index element={<OnboardingReportsPage />} />
          </Route>
          <Route path="users-reports">
            <Route index element={<UsersReportsPage />} />
          </Route>
          <Route path="ai-coach">
            <Route path="prompts" element={<AICoachPromptsPage />} />
            <Route path="level-vocabulary" element={<AICoachLevelVocabularyPage />} />
            <Route path="topics" element={<AITopicsPage />} />
            <Route path="custom-topics" element={<AICustomTopicsPage />} />
            <Route path="voices" element={<AILanguageVoicesPage />} />
          </Route>
          {/* <Route path="organizations/">
            <Route index element={<Organizations />} />
            <Route
              path="students/:orgId"
              element={<OrganizationStudentsPage />}
            />
            <Route path="create" element={<CreateOrganization />} />
          </Route>
          <Route path="admins/*">
            <Route index element={<Admins />} />
          </Route>
          <Route path="vouchers-codes">
            <Route index element={<VouchersCodes />} />
            <Route path=":id" element={<ShowVoucherCode />} />
          </Route>
          <Route path="students/*">
            <Route index element={<Students />} />
            <Route path=":id/*">
              <Route index element={<ShowStudent />} />
              <Route path="private" element={<PrivateStudentTutors />} />
            </Route>
          </Route>
          <Route path="applying-tutors/*">
            <Route index element={<ApplyingTutors />} />
            <Route path=":id/*">
              <Route index element={<ShowApplyingTutor />} />
            </Route>
          </Route>
          <Route path="withdrawal-requests/*">
            <Route index element={<TutorWithdrawalRequests />} />
            <Route path=":id/*">
              <Route path="invoice" element={<Invoice />}></Route>
            </Route>
          </Route>
          <Route path="bookings/*">
            <Route index element={<Bookings />} />
          </Route>
          <Route path="cancellation-requests/*">
            <Route index element={<CancellationRequests />} />
            <Route path=":id/*">
              <Route index element={<CancellationRequestPage />}></Route>
            </Route>
          </Route>
          <Route path="sessions/*">
            <Route index element={<Sessions />} />
          </Route>
          <Route path="chat-support/*">
            <Route index element={<ChatSupportPage />} />
          </Route>
          <Route path="find-for-me-requests/">
            <Route index element={<FindForMeNowRequests />} />
          </Route>
          <Route path="tutors-transactions">
            <Route index element={<TutorsTransactionHistory />} />
          </Route>
          <Route path="students-transactions">
            <Route index element={<StudentsTransactionHistory />} />
          </Route>
          <Route path="available-slots">
            <Route index element={<AvailableSlots />} />
          </Route>
          <Route path="404" element={<>404</>} />
          <Route path="505" element={<>505</>} />
        </Route> */}
        </Route>
      </Route>
    </Routes>
  );
}
